import React from 'react';
import { Button } from '@components/ui/button';
import { Switch } from '@components/ui/switch';
import { Input } from '@components/ui/input';
import { Label } from '@components/ui/label';
import { Badge } from '@components/ui/badge';
import { Slider } from '@components/ui/slider';
import type { Block, ChildBlock, ColumnsBlock } from '@staty/shared/schemas/block';
import { newBlockId } from '@staty/shared/utils/blockUtils';
import useEditorStore, {
  ColAddr,
  EntityRef,
  LayoutDraft,
  PostDraft,
  PostEntity,
} from './editorStore';
import type { ActiveLayoutSurface } from './CanvasStage';

/**
 * Right contextual panel. Context switch:
 * - a BLOCK is selected → that block's per-type settings;
 * - a POST is selected → post settings (publish state is an explicit,
 *   immediate action — never autosaved; content fields flow through the
 *   draft + autosave);
 * - a LAYOUT is selected → page settings (nav visibility/order for pages,
 *   context notes for the fallback home / division callouts).
 * Cover add/replace/remove lives on the card itself (PostCardFrame), not
 * here — one home per control.
 */

interface Props {
  post: PostEntity | null;
  layoutSurface: ActiveLayoutSurface | null;
  onPublishToggle: (post: PostEntity, publish: boolean) => void;
  onPinToggle: (post: PostEntity, pinned: boolean) => void;
  onDelete: (post: PostEntity) => void;
  onDeleteLayout: (layoutId: string) => void;
}

interface FoundBlock {
  block: Block | ChildBlock;
  colAddr?: ColAddr;
}

function findSelectedBlock(blocks: Block[], blockId: string | null): FoundBlock | null {
  if (!blockId) return null;
  for (const b of blocks) {
    if (b.id === blockId) return { block: b };
    if (b.type === 'columns') {
      for (const col of b.columns) {
        const child = col.blocks.find((c) => c.id === blockId);
        if (child) return { block: child, colAddr: { blockId: b.id, columnId: col.id } };
      }
    }
  }
  return null;
}

export default function SettingsPanel({ post, layoutSurface, onPublishToggle, onPinToggle, onDelete, onDeleteLayout }: Props) {
  const selected = useEditorStore((s) => s.selected);
  const draft = useEditorStore((s) => (selected ? s.drafts[`${selected.kind}:${selected.id}`] : undefined));
  const selectedBlockId = useEditorStore((s) => s.selectedBlockId);
  const patchDraft = useEditorStore((s) => s.patchDraft);
  const updateBlock = useEditorStore((s) => s.updateBlock);

  if (!selected || !draft) {
    return (
      <aside className="canvas-settings">
        <p className="canvas-settings__empty">Select a post or page on the canvas to edit its settings.</p>
      </aside>
    );
  }

  const found = findSelectedBlock(draft.blocks, selectedBlockId);
  if (found) {
    return (
      <aside className="canvas-settings">
        <BlockSettings
          entity={selected}
          block={found.block}
          colAddr={found.colAddr}
          onPatch={(patch) => updateBlock(selected, found.block.id, patch, found.colAddr)}
        />
      </aside>
    );
  }

  if (selected.kind === 'layout') {
    return (
      <LayoutSettings
        entity={selected}
        draft={draft as LayoutDraft}
        layoutSurface={layoutSurface}
        onDeleteLayout={onDeleteLayout}
        patchDraft={patchDraft}
      />
    );
  }

  if (!post) {
    return (
      <aside className="canvas-settings">
        <p className="canvas-settings__empty">Select a post on the canvas to edit its settings.</p>
      </aside>
    );
  }

  const postDraft = draft as PostDraft;
  const published = post.status === 'published';
  return (
    <aside className="canvas-settings">
      <div className="canvas-settings__section">
        <div className="canvas-settings__row">
          <span className="canvas-settings__title">Post</span>
          <Badge variant={published ? 'default' : 'secondary'}>
            {published ? 'Published' : 'Draft'}
          </Badge>
        </div>
        <Button
          className="w-full"
          variant={published ? 'outline' : 'default'}
          onClick={() => onPublishToggle(post, !published)}
        >
          {published ? 'Unpublish' : 'Publish'}
        </Button>
      </div>

      {/* Pinned post (INT-0048): one per org, first on /home, shown in full. */}
      <div className="canvas-settings__section">
        <div className="canvas-settings__row">
          <Label htmlFor="canvas-pinned">Pin to top of home</Label>
          <Switch
            id="canvas-pinned"
            checked={post.pinned === true}
            onCheckedChange={(v: boolean) => onPinToggle(post, v)}
            data-tour="editor-pin-post"
          />
        </div>
        <p className="canvas-settings__hint">
          {post.pinned && !published
            ? 'Pinned, not published yet. Publish it to show it at the top of your home page.'
            : 'The pinned post stays at the top of your home page and shows in full. One post can be pinned; this replaces the current pinned post.'}
        </p>
      </div>

      <div className="canvas-settings__section">
        <div className="canvas-settings__row">
          <Label htmlFor="canvas-show-title">Show title</Label>
          <Switch
            id="canvas-show-title"
            checked={postDraft.showTitle}
            onCheckedChange={(v: boolean) => patchDraft(selected, { showTitle: v })}
          />
        </div>
        <div className="canvas-settings__row">
          <Label htmlFor="canvas-show-date">Show date</Label>
          <Switch
            id="canvas-show-date"
            checked={postDraft.showDate}
            onCheckedChange={(v: boolean) => patchDraft(selected, { showDate: v })}
          />
        </div>
        <div className="canvas-settings__field">
          <Label htmlFor="canvas-post-date">Display date</Label>
          <Input
            id="canvas-post-date"
            type="date"
            value={postDraft.publishedAt || ''}
            onChange={(e) => patchDraft(selected, { publishedAt: e.target.value || null })}
          />
          <p className="canvas-settings__hint">
            Orders the feed (newest first) even when hidden from the card.
          </p>
        </div>
      </div>

      <div className="canvas-settings__section">
        <Button variant="destructive" className="w-full" onClick={() => onDelete(post)}>
          Delete post
        </Button>
      </div>
    </aside>
  );
}

/* ---- layout (page / fallback / callout) settings --------------------------- */

function LayoutSettings({
  entity,
  draft,
  layoutSurface,
  onDeleteLayout,
  patchDraft,
}: {
  entity: EntityRef;
  draft: LayoutDraft;
  layoutSurface: ActiveLayoutSurface | null;
  onDeleteLayout: (layoutId: string) => void;
  patchDraft: (ref: EntityRef, patch: Partial<LayoutDraft>) => void;
}) {
  const kind = layoutSurface?.kind ?? 'page';

  if (kind === 'fallback-home') {
    return (
      <aside className="canvas-settings">
        <div className="canvas-settings__section">
          <span className="canvas-settings__title">Home (fallback page)</span>
          <p className="canvas-settings__hint">
            Visitors see this page only while no posts are published. It can&apos;t be deleted.
            It&apos;s what new visitors land on before your first post.
          </p>
        </div>
      </aside>
    );
  }

  if (kind === 'division-callout') {
    return (
      <aside className="canvas-settings">
        <div className="canvas-settings__section">
          <span className="canvas-settings__title">Division callout</span>
          <p className="canvas-settings__hint">
            Shown above this division&apos;s public schedule. An empty callout shows nothing.
          </p>
        </div>
        <div className="canvas-settings__section">
          <Button variant="destructive" className="w-full" onClick={() => onDeleteLayout(entity.id)}>
            Delete callout
          </Button>
        </div>
      </aside>
    );
  }

  return (
    <aside className="canvas-settings">
      <div className="canvas-settings__section">
        <div className="canvas-settings__row">
          <span className="canvas-settings__title">Page</span>
          <Badge variant={draft.active ? 'default' : 'secondary'}>
            {draft.active ? 'In navigation' : 'Hidden'}
          </Badge>
        </div>
        <div className="canvas-settings__row">
          <Label htmlFor="canvas-page-active">Show in navigation</Label>
          <Switch
            id="canvas-page-active"
            checked={draft.active}
            onCheckedChange={(v: boolean) => patchDraft(entity, { active: v })}
          />
        </div>
        <p className="canvas-settings__hint">
          New pages start hidden. Flip this on when the page is ready for visitors.
        </p>
      </div>

      <div className="canvas-settings__section">
        <div className="canvas-settings__field">
          <Label htmlFor="canvas-page-name">Page name</Label>
          <Input
            id="canvas-page-name"
            type="text"
            value={draft.name}
            placeholder="Page name"
            onChange={(e) => patchDraft(entity, { name: e.target.value })}
          />
          <p className="canvas-settings__hint">
            Also the page&apos;s address: /l/{(draft.name || 'page-name').replace(/\s+/g, '-').toLowerCase()}
          </p>
        </div>
        <div className="canvas-settings__field">
          <Label htmlFor="canvas-page-order">Navigation order</Label>
          {/* Text + numeric keypad, never type="number" (CLAUDE.md: the
              spinner corrupts values on accidental scroll). */}
          <Input
            id="canvas-page-order"
            type="text"
            inputMode="numeric"
            value={String(draft.order ?? 0)}
            onChange={(e) => {
              const digits = e.target.value.replace(/[^0-9]/g, '');
              patchDraft(entity, { order: digits === '' ? 0 : Number(digits) });
            }}
          />
          <p className="canvas-settings__hint">Lower numbers appear first in the nav.</p>
        </div>
      </div>

      <div className="canvas-settings__section">
        <Button variant="destructive" className="w-full" onClick={() => onDeleteLayout(entity.id)}>
          Delete page
        </Button>
      </div>
    </aside>
  );
}

/* ---- per-type block settings ---------------------------------------------- */

// The legacy width preset's approximate percent — the slider/quick-buttons
// display this until a widthPct is written (which then overrides the preset).
function presetPct(width: 'full' | 'medium' | 'small' | undefined): number {
  if (width === 'small') return 33;
  if (width === 'medium') return 66;
  return 100;
}

function Segmented<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="canvas-segmented" role="group">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className={`canvas-segmented__item ${value === o.value ? 'canvas-segmented__item--active' : ''}`}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

interface BlockSettingsProps {
  entity: EntityRef;
  block: Block | ChildBlock;
  colAddr?: ColAddr;
  onPatch: (patch: Partial<Block>) => void;
}

function BlockSettings({ entity, block, colAddr, onPatch }: BlockSettingsProps) {
  const selectBlock = useEditorStore((s) => s.selectBlock);

  const header = (title: string) => (
    <div className="canvas-settings__row">
      <span className="canvas-settings__title">{title}</span>
      <Button variant="ghost" size="sm" onClick={() => selectBlock(null)}>
        Done
      </Button>
    </div>
  );

  switch (block.type) {
    case 'image':
      return (
        <div className="canvas-settings__section">
          {header('Image')}
          <div className="canvas-settings__field">
            <Label htmlFor="blk-img-alt">Alt text</Label>
            <Input
              id="blk-img-alt"
              type="text"
              value={block.alt || ''}
              placeholder="Describe the image"
              onChange={(e) => onPatch({ alt: e.target.value })}
            />
          </div>
          <div className="canvas-settings__field">
            <Label>Width</Label>
            {/* widthPct overrides the legacy full/medium/small preset; the
                quick buttons just write common percents. The canvas drag
                handle writes the same field. */}
            <Slider
              value={[block.widthPct ?? presetPct(block.width)]}
              min={10}
              max={100}
              step={1}
              onValueChange={(v: number[]) => onPatch({ widthPct: v[0] })}
            />
            <div className="canvas-settings__row">
              <Segmented
                value={String(block.widthPct ?? presetPct(block.width))}
                options={[
                  { value: '33', label: '⅓' },
                  { value: '50', label: '½' },
                  { value: '66', label: '⅔' },
                  { value: '100', label: 'Full' },
                ]}
                onChange={(v) => onPatch({ widthPct: Number(v) })}
              />
              <span className="canvas-settings__pct">{block.widthPct ?? presetPct(block.width)}%</span>
            </div>
            <p className="canvas-settings__hint">
              Or drag the handle on the image&apos;s corner.
            </p>
          </div>
          <div className="canvas-settings__field">
            <Label>Alignment</Label>
            <Segmented
              value={block.alignment || 'center'}
              options={[
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' },
              ]}
              onChange={(alignment) => onPatch({ alignment })}
            />
          </div>
          <div className="canvas-settings__field">
            <Label htmlFor="blk-img-link">Link (optional)</Label>
            <Input
              id="blk-img-link"
              type="text"
              value={block.link || ''}
              placeholder="https://…"
              onChange={(e) => onPatch({ link: e.target.value || undefined })}
            />
          </div>
        </div>
      );

    case 'embed':
      return (
        <div className="canvas-settings__section">
          {header('Video / Embed')}
          <div className="canvas-settings__field">
            <Label htmlFor="blk-embed-url">URL</Label>
            <Input
              id="blk-embed-url"
              type="text"
              value={block.url}
              placeholder="https://www.youtube.com/watch?v=…"
              onChange={(e) => onPatch({ url: e.target.value })}
            />
            <p className="canvas-settings__hint">YouTube, Vimeo, or a Maps embed link.</p>
          </div>
        </div>
      );

    case 'button':
      return (
        <div className="canvas-settings__section">
          {header('Button')}
          <div className="canvas-settings__field">
            <Label htmlFor="blk-btn-url">Link URL</Label>
            <Input
              id="blk-btn-url"
              type="text"
              value={block.url}
              placeholder="/register or https://…"
              onChange={(e) => onPatch({ url: e.target.value })}
            />
          </div>
          <div className="canvas-settings__field">
            <Label>Style</Label>
            <Segmented
              value={block.style || 'primary'}
              options={[
                { value: 'primary', label: 'Primary' },
                { value: 'outline', label: 'Outline' },
              ]}
              onChange={(style) => onPatch({ style })}
            />
          </div>
          <div className="canvas-settings__field">
            <Label>Alignment</Label>
            <Segmented
              value={block.alignment || 'left'}
              options={[
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' },
              ]}
              onChange={(alignment) => onPatch({ alignment })}
            />
          </div>
        </div>
      );

    case 'columns':
      return <ColumnsSettings block={block} header={header} onPatch={onPatch} />;

    case 'richtext':
      return (
        <div className="canvas-settings__section">
          {header('Text')}
          <p className="canvas-settings__hint">
            Edit directly on the canvas. Select text for the formatting toolbar.
          </p>
        </div>
      );

    case 'divider':
    default:
      return (
        <div className="canvas-settings__section">
          {header('Divider')}
          <p className="canvas-settings__hint">No settings. Use the block toolbar to move or remove it.</p>
        </div>
      );
  }
}

// Ratio presets per column count — spans in fr units ([1,2] = 33/67).
const RATIOS_2: { label: string; spans: number[] }[] = [
  { label: '1 : 1', spans: [1, 1] },
  { label: '1 : 2', spans: [1, 2] },
  { label: '2 : 1', spans: [2, 1] },
  { label: '1 : 3', spans: [1, 3] },
  { label: '3 : 1', spans: [3, 1] },
];
const RATIOS_3: { label: string; spans: number[] }[] = [
  { label: '1 : 1 : 1', spans: [1, 1, 1] },
  { label: '2 : 1 : 1', spans: [2, 1, 1] },
  { label: '1 : 2 : 1', spans: [1, 2, 1] },
  { label: '1 : 1 : 2', spans: [1, 1, 2] },
];
const RATIOS_4: { label: string; spans: number[] }[] = [
  { label: '1 : 1 : 1 : 1', spans: [1, 1, 1, 1] },
  { label: '2 : 1 : 1 : 1', spans: [2, 1, 1, 1] },
  { label: '1 : 1 : 1 : 2', spans: [1, 1, 1, 2] },
  { label: '1 : 2 : 2 : 1', spans: [1, 2, 2, 1] },
];

function ColumnsSettings({
  block,
  header,
  onPatch,
}: {
  block: ColumnsBlock;
  header: (title: string) => React.ReactNode;
  onPatch: (patch: Partial<Block>) => void;
}) {
  const count = block.columns.length;
  const last = block.columns[count - 1];
  // Removing a column would silently delete its content — only allow it when
  // the last cell is empty (matching the schema's 2..4 bounds).
  const canRemove = count > 2 && last.blocks.length === 0;

  const ratios = count === 2 ? RATIOS_2 : count === 3 ? RATIOS_3 : RATIOS_4;
  const currentSpans = block.columns.map((c) => c.span ?? 1).join(':');
  const applyRatio = (spans: number[]) =>
    onPatch({
      columns: block.columns.map((c, i) => ({ ...c, span: spans[i] })),
    } as Partial<Block>);

  return (
    <div className="canvas-settings__section">
      {header('Columns')}
      <div className="canvas-settings__row">
        <Label>Column count</Label>
        <div className="canvas-segmented" role="group">
          <button
            type="button"
            className="canvas-segmented__item"
            disabled={!canRemove}
            title={count > 2 && !canRemove ? 'Empty the last column first' : undefined}
            onClick={() =>
              canRemove && onPatch({ columns: block.columns.slice(0, -1) } as Partial<Block>)
            }
          >
            −
          </button>
          <span className="canvas-segmented__count">{count}</span>
          <button
            type="button"
            className="canvas-segmented__item"
            disabled={count >= 4}
            onClick={() =>
              count < 4 &&
              onPatch({
                columns: [...block.columns, { id: newBlockId(), blocks: [] }],
              } as Partial<Block>)
            }
          >
            +
          </button>
        </div>
      </div>
      {count > 2 && !canRemove && (
        <p className="canvas-settings__hint">To remove a column, empty the last one first.</p>
      )}
      <div className="canvas-settings__field">
        <Label>Column widths</Label>
        <div className="canvas-ratio-grid" role="group">
          {ratios.map((r) => (
            <button
              key={r.label}
              type="button"
              className={`canvas-ratio ${
                currentSpans === r.spans.join(':') ? 'canvas-ratio--active' : ''
              }`}
              onClick={() => applyRatio(r.spans)}
              title={r.label}
            >
              <span
                className="canvas-ratio__bars"
                style={{ gridTemplateColumns: r.spans.map((s) => `${s}fr`).join(' ') }}
              >
                {r.spans.map((s, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <span key={i} />
                ))}
              </span>
              <span className="canvas-ratio__label">{r.label}</span>
            </button>
          ))}
        </div>
        <p className="canvas-settings__hint">
          e.g. 1 : 2 gives a narrow image column beside wide text. Phones always stack.
        </p>
      </div>
    </div>
  );
}
